import type { ModifierState } from "@/domain/profile-resolver/index";
import { tokenToCodes } from "@/domain/tokens";
import type { KeyboardLayout } from "@/domain/types";

/**
 * One parsed alternative of a binding: every physical key it lands on plus its
 * combo. Usually one code; several when the active mapping emits the bound
 * character from more than one key (a gaming board layer repeating a keycode).
 */
export interface SC2Hotkey {
  codes: string[];
  mods: ModifierState;
}

/**
 * The structured content of a .SC2Hotkeys file, tokens already resolved to codes.
 * Each entry is the value's mappable alternatives in file order; an alternate is
 * an ordinary Binding with no rank (domain.md), so nothing marks the first one.
 */
export interface SC2Table {
  /** [Commands] section: command id, then unit context ("_def" when global). Slice 09 renders these. */
  commands: Record<string, Record<string, SC2Hotkey[]>>;
  /** [Hotkeys] section: the global commands (control groups, cameras, selection). */
  hotkeys: Record<string, SC2Hotkey[]>;
}

/**
 * The base preset a file is a delta against: the `[Settings] Suffix=` value, or "" when
 * absent (Standard). `_SC1` = Classic, `_GLS`/`_GRS` = Grid. The parser otherwise skips [Settings].
 */
export function readSuffix(text: string): string {
  let inSettings = false;
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    const header = line.match(/^\[(.+)\]$/);
    if (header?.[1]) {
      inSettings = header[1].toLowerCase() === "settings";
      continue;
    }
    if (inSettings) {
      const m = line.match(/^Suffix\s*=\s*(.*)$/i);
      if (m?.[1] !== undefined) return m[1].trim();
    }
  }
  return "";
}

/** Cheap sniff for the import pipeline: a section header only SC2 hotkey files carry. */
export function looksLikeSC2Hotkeys(text: string): boolean {
  // case-insensitive to match the parser, which lowercases section names
  return /^\s*\[(Hotkeys|Commands)\]\s*$/im.test(text);
}

/**
 * One comma-alternative of a binding value, both views of the same lexeme. `rawKey` / `rawMods`
 * are the "+"-split exactly as written, already divided at the grammar's key-is-last boundary:
 * rejoining `[...rawMods, rawKey]` with "+" and the alternatives with "," restores the value
 * byte-for-byte, which the serialize walker depends on. `key` / `mods` are the trimmed reading
 * (modifier names in file order), and `modsKey` the order-insensitive modifier identity the
 * sibling-file aligners check index-paired alternatives against.
 */
export interface ValueAlt {
  rawMods: string[];
  rawKey: string;
  key: string;
  mods: string[];
  modsKey: string;
}

/**
 * Tokenize a binding value into its comma alternatives. The one encoding of the .SC2Hotkeys
 * value grammar (comma-separated alternatives; "+"-separated parts; the key is the LAST part,
 * modifiers come first): altParts, rewriteValues and the sibling-file aligners all read a value
 * through this split instead of re-deriving it.
 */
export function valueAlts(value: string): ValueAlt[] {
  return value.split(",").map((alt) => {
    const rawParts = alt.split("+");
    const rawKey = rawParts[rawParts.length - 1] ?? "";
    const rawMods = rawParts.slice(0, -1);
    const key = rawKey.trim();
    const mods = rawMods.map((p) => p.trim());
    return { rawMods, rawKey, key, mods, modsKey: [...mods].sort().join("+") };
  });
}

/**
 * A binding value's alternatives as trimmed modifier flags + key token - the ModifierState view
 * of valueAlts that parseToken (-> codes) and the ingest's splitAlternates (-> tokens) share.
 */
export function altParts(value: string): Array<{ mods: ModifierState; key: string }> {
  return valueAlts(value).map(({ key, mods }) => ({
    key,
    mods: {
      shift: mods.includes("Shift"),
      ctrl: mods.includes("Control"),
      alt: mods.includes("Alt"),
    },
  }));
}

/**
 * Resolve one binding value: every comma-separated alternative that maps to a key,
 * each with its own combo (Modifier+...+Key, modifiers named Control, Shift, Alt).
 * An unmappable alternative (a mouse button) skips without blocking its siblings.
 */
export function parseToken(value: string, keyboardLayout: KeyboardLayout): SC2Hotkey[] {
  const alternatives: SC2Hotkey[] = [];
  for (const { mods, key } of altParts(value)) {
    const codes = tokenToCodes(key, keyboardLayout);
    if (codes.length > 0) alternatives.push({ codes, mods });
  }
  return alternatives;
}

/** The raw character-space content of a .SC2Hotkeys file: values exactly as written. */
export interface SC2RawTable {
  /** command id, then unit context ("_def" when global), then the verbatim value */
  commands: Record<string, Record<string, string>>;
  /** hotkey id to its verbatim value */
  hotkeys: Record<string, string>;
}

interface SC2EntryLine {
  section: "commands" | "hotkeys";
  left: string;
  right: string;
}

/** One pass over the key=value lines in the [Commands] and [Hotkeys] sections. */
function* entryLines(text: string): Generator<SC2EntryLine> {
  let section: string | null = null;
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line || line.startsWith("//")) continue;
    const header = line.match(/^\[(.+)\]$/);
    if (header?.[1]) {
      section = header[1].toLowerCase();
      continue;
    }
    if (section !== "commands" && section !== "hotkeys") continue;
    const eq = line.indexOf("=");
    if (eq < 0) continue;
    yield { section, left: line.slice(0, eq).trim(), right: line.slice(eq + 1).trim() };
  }
}

/** What a rewriteValues callback sees beside the raw key slot. */
export interface RewriteContext {
  /** The section the value lives in; the collision domain differs per section (decision 0045). */
  section: "commands" | "hotkeys";
  /** The alternative's order-insensitive modifier identity (valueAlts' modsKey), so a rewrite
   * can reason about the full combo without re-deriving the sort. */
  modsKey: string;
}

/**
 * Rewrite only the key token of every binding value in the [Commands]/[Hotkeys] sections, preserving
 * the file's character-space form exactly: the key id, the "=", surrounding
 * whitespace, comma-separated alternative order, every modifier, comments and non-binding sections all
 * pass through untouched. Within an alternative the final "+"-part is the key (modifiers come first);
 * only it is rewritten, via `rewriteKey`, which receives the RAW (untrimmed) key slot plus its
 * {@link RewriteContext}. The one serialize walker both the locale axis (convert) and the hand
 * axis (applyHandMirror) go through; the value grammar itself lives in valueAlts, whose raw view
 * feeds the rewrite byte-faithfully.
 */
export function rewriteValues(
  text: string,
  rewriteKey: (key: string, ctx: RewriteContext) => string,
): string {
  let section: "commands" | "hotkeys" | null = null;
  return text
    .split("\n")
    .map((line) => {
      const trimmed = line.trim();
      const header = trimmed.match(/^\[(.+)\]$/);
      if (header?.[1]) {
        const named = header[1].toLowerCase();
        section = named === "commands" || named === "hotkeys" ? named : null;
        return line;
      }
      if (section == null) return line;
      if (!trimmed || trimmed.startsWith("//")) return line;
      const eq = line.indexOf("=");
      if (eq < 0) return line;
      const left = line.slice(0, eq);
      const right = line.slice(eq + 1);
      const lead = right.match(/^\s*/)?.[0] ?? "";
      const trail = right.match(/\s*$/)?.[0] ?? "";
      const core = right.slice(lead.length, right.length - trail.length);
      const ctx = { section };
      // the raw view: the key slot is rewritten in place, every other byte passes through
      const rewritten = valueAlts(core)
        .map(({ rawMods, rawKey, modsKey }) =>
          [...rawMods, rewriteKey(rawKey, { ...ctx, modsKey })].join("+"),
        )
        .join(",");
      return `${left}=${lead}${rewritten}${trail}`;
    })
    .join("\n");
}

/**
 * One flat record over both sections of a raw table, section-prefixed so a [Commands] id can never
 * collide with a [Hotkeys] id ("commands:Build/SCV", "hotkeys:Idle"). The sibling-file aligners
 * (deriveLayout, learnHandMirror) pair two files by these keys, so the scheme only needs to be one
 * thing applied to both sides.
 */
export function flattenRawTable(table: SC2RawTable): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [cmd, byUnit] of Object.entries(table.commands))
    for (const [unit, value] of Object.entries(byUnit)) out[`commands:${cmd}/${unit}`] = value;
  for (const [id, value] of Object.entries(table.hotkeys)) out[`hotkeys:${id}`] = value;
  return out;
}

/** The [Commands] left side: command id, then unit context, "_def" when slash-less. */
function splitCommandId(left: string): { cmd: string; unit: string } {
  const slash = left.indexOf("/");
  return slash < 0
    ? { cmd: left, unit: "_def" }
    : { cmd: left.slice(0, slash), unit: left.slice(slash + 1) };
}

/** Parse a .SC2Hotkeys file through a Keyboard Layout (the file is character-space).
 * Exactly the raw parse followed by resolution; kept as one call so a live upload and a merged-delta
 * base share the same bucketing and token resolution rather than two hand-synced walks. */
export function parse(text: string, keyboardLayout: KeyboardLayout): SC2Table {
  return resolveRaw(parseRaw(text), keyboardLayout);
}

/**
 * Resolve an already-parsed raw table's verbatim values to codes through a Keyboard Layout - the
 * back half of parse(), split out so a *merged* raw table (a Standard-defaults base under an
 * uploaded delta) can be resolved without re-serialising it to text.
 */
export function resolveRaw(raw: SC2RawTable, keyboardLayout: KeyboardLayout): SC2Table {
  const commands: SC2Table["commands"] = {};
  const hotkeys: SC2Table["hotkeys"] = {};
  for (const [cmd, units] of Object.entries(raw.commands)) {
    const bucket: Record<string, SC2Hotkey[]> = {};
    for (const [unit, value] of Object.entries(units))
      bucket[unit] = parseToken(value, keyboardLayout);
    commands[cmd] = bucket;
  }
  for (const [id, value] of Object.entries(raw.hotkeys))
    hotkeys[id] = parseToken(value, keyboardLayout);
  return { commands, hotkeys };
}

/**
 * Parse keeping values as written. Sibling-file alignment needs raw tokens: which
 * alternatives parseToken keeps is layout-dependent, and a sibling's layout is
 * exactly the unknown being recovered.
 */
export function parseRaw(text: string): SC2RawTable {
  const commands: SC2RawTable["commands"] = {};
  const hotkeys: SC2RawTable["hotkeys"] = {};
  for (const entry of entryLines(text)) {
    if (entry.section === "commands") {
      const { cmd, unit } = splitCommandId(entry.left);
      const bucket = commands[cmd] ?? {};
      bucket[unit] = entry.right;
      commands[cmd] = bucket;
    } else {
      hotkeys[entry.left] = entry.right;
    }
  }
  return { commands, hotkeys };
}
